import React, { useState } from 'react';
import { LayoutChangeEvent, View } from 'react-native';

import { designSystem, withOpacity } from '@/styles/styles';
import { logger } from '@/utils/logging';

import { RadialGradientProps } from './types';
import { createDefaultPositions } from './utils';

const defaultColors = [designSystem.colors.neutral900, withOpacity(designSystem.colors.neutral900, 0)];

/**
 * Web implementation of RadialGradient using a CSS radial-gradient instead of a Skia canvas —
 * Skia/CanvasKit is not loaded on web (see LinearGradient.web.tsx for the same approach).
 * Mirrors the native drawing: a circle of `size` anchored to the top-left of the styled box.
 */
export const RadialGradient = (props: RadialGradientProps) => {
    const [width, setWidth] = useState(1);
    const [height, setHeight] = useState(1);

    const measure = (event: LayoutChangeEvent) => {
        setWidth(event.nativeEvent.layout.width);
        setHeight(event.nativeEvent.layout.height);
    };

    const size = props.size ?? Math.min(width, height);
    const radius = size / 2;

    let { colors: gradientColors = defaultColors, positions: gradientPositions } = props;
    if (!gradientPositions || gradientPositions.length !== gradientColors.length) {
        logger.debug(
            '[RadialGradient]',
            'The number of positions must match the number of colors. Using default positions.'
        );
        gradientPositions = createDefaultPositions(gradientColors.length);
    }

    const colorStops = gradientColors
        .map((color, index) => `${color} ${(gradientPositions?.[index] ?? 0) * 100}%`)
        .join(',');

    return (
        <View
            onLayout={measure}
            style={[
                props.style,
                { backgroundImage: `radial-gradient(circle ${radius}px at ${radius}px ${radius}px, ${colorStops})` },
            ]}
        />
    );
};
